<script setup>
import { computed, ref } from 'vue'

/**
 * A picture on the editor's page with its width set by hand (D-207): while it is picked, a handle
 * on each side to drag (the width in millimetres shown as it changes, whole millimetres), and a
 * bar above it — a quarter, a half, three quarters or the whole width of the text, the width typed
 * in millimetres, or the picture's own. Nothing of it prints or moves the text: the bar and the
 * handles stand over the page.
 */
const props = defineProps({
  src: { type: String, required: true },
  widthMm: { type: [Number, String, null], default: null },
  maxMm: { type: Number, default: 170 },
  active: Boolean,
})
const emit = defineEmits(['update'])
const MIN = 15
const PX_PER_MM = 96 / 25.4
const img = ref(null)
const live = ref(null) // the width while it is dragged
const width = computed(() => live.value ?? (props.widthMm ? Number(props.widthMm) : null))
const clamp = (mm) => Math.max(MIN, Math.min(props.maxMm, Math.round(mm)))
const presets = [25, 50, 75, 100]
const presetMm = (p) => clamp((props.maxMm * p) / 100)

function start(e, side) {
  const el = img.value
  if (!el || e.button > 0) return
  e.preventDefault()
  e.stopPropagation()
  // On screen: the page drawn at its zoom.
  const rect = el.getBoundingClientRect()
  const screen = PX_PER_MM * (rect.width / (el.offsetWidth || rect.width))
  const from = el.offsetWidth / PX_PER_MM
  // A picture in the middle of the line grows on both sides at once.
  const centred = getComputedStyle(el.parentElement.parentElement || el).textAlign === 'center'
  const x0 = e.clientX
  const move = (m) => {
    live.value = clamp(from + ((m.clientX - x0) * side * (centred ? 2 : 1)) / screen)
  }
  const end = () => {
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', end)
    if (live.value !== null && live.value !== Number(props.widthMm)) emit('update', live.value)
    live.value = null
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', end)
}
function typed(e) {
  const v = Number(String(e.target.value).replace(',', '.'))
  emit('update', Number.isFinite(v) && v > 0 ? clamp(v) : null)
}
</script>

<template>
  <span class="doc-sized" :data-active="active">
    <img ref="img" :src="src" alt="" :style="width ? { width: `${width}mm` } : {}" draggable="false" />
    <template v-if="active">
      <span v-for="side in [-1, 1]" :key="side" :class="['doc-size-handle', side < 0 ? 'doc-size-handle-l' : 'doc-size-handle-r']" :title="$t('offers.imageWidthDrag')" data-testid="image-size-handle" @pointerdown="start($event, side)" @mousedown.stop.prevent></span>
      <span v-if="live !== null" class="doc-size-label">{{ live }} mm</span>
      <span class="doc-size-bar" contenteditable="false" data-testid="image-size-bar" @mousedown.stop>
        <button v-for="p in presets" :key="p" type="button" :class="['doc-tool', Number(widthMm) === presetMm(p) ? 'doc-tool-on' : '']" :title="$t('offers.imageWidthOf', { n: p })" :data-testid="`image-width-${p}`" @click.stop="emit('update', presetMm(p))">{{ p }}%</button>
        <input class="doc-size-mm" type="number" :min="MIN" :max="maxMm" :value="widthMm || ''" placeholder="mm" :title="$t('offers.imageWidth')" data-testid="image-width-mm" @change="typed" @keydown.enter.prevent="typed" @keydown.stop />
        <span class="doc-tool-note">mm</span>
        <button type="button" :class="['doc-tool', !widthMm ? 'doc-tool-on' : '']" :title="$t('offers.imageWidthAuto')" data-testid="image-width-auto" @click.stop="emit('update', null)"><i class="fa-solid fa-expand" aria-hidden="true"></i></button>
      </span>
    </template>
  </span>
</template>

<style>
/* a picture's width set by hand (D-207): over the page, never printed */
.doc-canvas .doc-sized { position: relative; display: inline-block; max-width: 100%; line-height: 0; }
.doc-canvas .doc-sized img { max-width: 100%; }
.doc-canvas .doc-sized[data-active="true"] img { outline: 2px solid #6366f1; outline-offset: 2px; }
.doc-canvas .doc-size-handle { position: absolute; top: 50%; width: 10px; height: 34px; margin-top: -17px; border-radius: 4px; background: #fff; border: 2px solid #6366f1; cursor: ew-resize; z-index: 4; touch-action: none; }
.doc-canvas .doc-size-handle-l { left: -7px; }
.doc-canvas .doc-size-handle-r { right: -7px; }
.doc-canvas .doc-size-label { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); background: rgba(15, 23, 42, .8); color: #fff; border-radius: 6px; padding: 2px 8px; font: 12px/1.6 system-ui, sans-serif; pointer-events: none; z-index: 4; }
.doc-canvas .doc-size-bar { position: absolute; bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 3px; white-space: nowrap; padding: 3px; border-radius: 8px; background: #fff; border: 1px solid #e2e8f0; box-shadow: 0 4px 12px rgba(15, 23, 42, .15); font: 12px/1.2 system-ui, sans-serif; line-height: 1.2; z-index: 5; }
.doc-canvas .doc-size-mm { width: 4.5em; border: 1px solid #cbd5e1; border-radius: 4px; padding: 1px 4px; font: inherit; text-align: right; }
</style>
